import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, Pencil, RefreshCcw, Search, ShieldBan, ShieldCheck, Users } from 'lucide-react';
import {
  superAdminGetUsers,
  superAdminSuspendUser,
  superAdminUpdateUserRole,
  superAdminUpdateUser,
} from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import StyledSelect from '../../components/StyledSelect';
import {
  ActionIconButton,
  exportRowsAsCsv,
  formatDate,
  formatDateTime,
  formatGlobalRole,
  GlobalRoleChip,
  OrgRoleChip,
  StatusChip,
  SuperAdminPanel,
} from '../components/SuperAdminUi';

const globalRoleOptions = [
  { value: '', label: 'All global roles' },
  { value: 'user', label: 'User' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
];

const orgRoleOptions = [
  { value: '', label: 'All organization roles' },
  { value: 'organization_head', label: 'Organization Head' },
  { value: 'admin', label: 'Admin' },
  { value: 'finance_manager', label: 'Finance Manager' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'project_manager', label: 'Project Manager' },
  { value: 'employee', label: 'Employee' },
  { value: 'viewer', label: 'Viewer' },
];

const orgStatusOptions = [
  { value: '', label: 'All org statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'pending', label: 'Pending' },
];

const userStatusOptions = [
  { value: '', label: 'All user statuses' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
];

const editableRoleOptions = globalRoleOptions.filter((o) => o.value === 'user' || o.value === 'admin');

const exportColumns = [
  { key: 'name', label: 'User Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone', value: (row) => row.phone || '-' },
  { key: 'organization_name', label: 'Organization' },
  { key: 'org_role', label: 'Organization Role' },
  { key: 'global_role', label: 'Global Role' },
  { key: 'organization_status', label: 'Organization Status' },
  { key: 'is_blocked', label: 'User Status', value: (row) => (Number(row.is_blocked) ? 'Suspended' : 'Active') },
  { key: 'last_login_at', label: 'Last Login', value: (row) => formatDateTime(row.last_login_at) },
  { key: 'created_at', label: 'Created Date', value: (row) => formatDate(row.created_at) },
];

export default function SuperAdminUsers() {
  const { user, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedUserId = Number(searchParams.get('userId') || 0);
  const scope = String(searchParams.get('scope') || '').trim().toLowerCase();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(() => ({
    role: '',
    org_role: '',
    org_status: '',
    user_status: '',
  }));
  const [meta, setMeta] = useState({ totalPages: 1, total: 0, page: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editRole, setEditRole] = useState('user');
  const [editForm, setEditForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });

  const load = async (nextPage = page, nextSearch = search, nextFilters = filters) => {
    setLoading(true);
    setError('');

    try {
      const response = await superAdminGetUsers({
        page: nextPage,
        limit: meta.limit,
        search: nextSearch || undefined,
        role: nextFilters.role || undefined,
        org_role: nextFilters.org_role || undefined,
        org_status: nextFilters.org_status || undefined,
        user_status: nextFilters.user_status || undefined,
      });

      const data = response?.data?.data || {};
      const sourceRows = data.users || [];
      const scopedRows = scope === 'admins'
        ? sourceRows.filter((row) => String(row.global_role || '') === 'admin' || String(row.org_role || '') === 'admin')
        : sourceRows;

      setRows(scopedRows);
      setMeta({
        totalPages: data.totalPages || 1,
        total: scope === 'admins' ? scopedRows.length : (data.total || 0),
        page: data.page || nextPage,
        limit: data.limit || meta.limit,
      });
      setPage(data.page || nextPage);
    } catch (requestError) {
      setRows([]);
      setError(requestError?.response?.data?.message || 'Failed to load organization users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, []);

  useEffect(() => {
    if (!selectedUserId || rows.length === 0) return;
    const target = rows.find((row) => Number(row.id) === selectedUserId);
    if (target) {
      setEditRow(target);
    }
  }, [selectedUserId, rows]);

  useEffect(() => {
    if (!editRow) return;
    setEditRole(editRow.global_role || 'user');
    setEditForm({
      first_name: editRow.first_name || (editRow.name ? editRow.name.split(' ')[0] : ''),
      last_name: editRow.last_name || (editRow.name ? editRow.name.split(' ').slice(1).join(' ') : ''),
      email: editRow.email || '',
      phone: editRow.phone || '',
    });
  }, [editRow]);

  const summary = useMemo(() => {
    const suspendedUsers = rows.filter((row) => Number(row.is_blocked)).length;
    const organizationHeads = rows.filter((row) => row.org_role === 'organization_head').length;
    const activeOrganizations = new Set(
      rows.filter((row) => row.organization_status === 'active').map((row) => row.organization_id)
    ).size;

    return [
      { label: 'Membership Rows', value: meta.total },
      { label: 'Organization Heads', value: organizationHeads },
      { label: 'Suspended Users', value: suspendedUsers },
      { label: 'Active Organizations', value: activeOrganizations },
    ];
  }, [meta.total, rows]);

  const handleRoleChange = async (targetRow, nextRole) => {
    if (!targetRow?.id) return;
    if (Number(targetRow.id) === Number(user?.id) && nextRole !== 'super_admin') {
      setError('You cannot remove your own super admin access while logged in.');
      return;
    }

    const confirmed = window.confirm(`Update global role for ${targetRow.name} to ${formatGlobalRole(nextRole)}?`);
    if (!confirmed) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await superAdminUpdateUserRole(targetRow.id, { role: nextRole });
      setSuccess('User role updated successfully.');
      setEditRow(null);
      await load(page);

      if (Number(targetRow.id) === Number(user?.id) && nextRole !== 'super_admin') {
        logout();
        window.location.href = '/login';
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to update global role.');
    } finally {
      setSaving(false);
    }
  };

  const handleSuspendToggle = async (targetRow, shouldBlock) => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await superAdminSuspendUser(targetRow.id, { is_blocked: shouldBlock });
      setSuccess(shouldBlock ? 'User suspended successfully.' : 'User reactivated successfully.');
      await load(page);
      if (editRow?.id === targetRow.id) {
        setEditRow({ ...targetRow, is_blocked: shouldBlock ? 1 : 0 });
      }
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to update user status.');
    } finally {
      setSaving(false);
    }
  };

  const handleUserUpdate = async () => {
    if (!editRow?.id) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await superAdminUpdateUser(editRow.id, {
        first_name: editForm.first_name.trim(),
        last_name: editForm.last_name.trim(),
        email: editForm.email.trim(),
        phone: editForm.phone.trim(),
      });
      setSuccess('User profile updated successfully.');
      setEditRow(null);
      await load(page);
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Failed to update user profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <h1 className="superadmin-title text-3xl font-bold text-slate-900">Platform User Control</h1>
          <p className="mt-1 text-slate-500">Cross-organization user visibility with role-safe control, organization context, and responsive operations.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => load(page)} className="ems-btn-secondary flex items-center gap-2 text-sm">
            <RefreshCcw size={14} /> Refresh
          </button>
          <button
            type="button"
            onClick={() => exportRowsAsCsv(rows, exportColumns, 'super-admin-users.csv')}
            className="ems-btn-primary text-sm"
          >
            Export CSV
          </button>
        </div>
      </div>

      <section className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        {summary.map((item) => (
          <div key={item.label} className="superadmin-kpi rounded-2xl p-4">
            <p className="text-xs uppercase tracking-[0.16em] text-slate-400">{item.label}</p>
            <p className="mt-3 text-2xl font-extrabold text-slate-900">{item.value}</p>
          </div>
        ))}
      </section>

      <section className="superadmin-glass rounded-3xl p-4 lg:p-5">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
          <label className="lg:col-span-2">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Search</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    setPage(1);
                    load(1);
                  }
                }}
                placeholder="Search name, email, organization, role"
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-10 pr-3 text-sm text-slate-800 outline-none transition focus:border-[#c44569] focus:ring-4 focus:ring-rose-100"
              />
            </div>
          </label>

          <Field label="Global Role">
            <StyledSelect value={filters.role} onChange={(value) => setFilters((current) => ({ ...current, role: value }))} options={globalRoleOptions} placeholder="Filter role" searchable />
          </Field>
          <Field label="Organization Role">
            <StyledSelect value={filters.org_role} onChange={(value) => setFilters((current) => ({ ...current, org_role: value }))} options={orgRoleOptions} placeholder="Filter org role" searchable />
          </Field>
          <Field label="Organization Status">
            <StyledSelect value={filters.org_status} onChange={(value) => setFilters((current) => ({ ...current, org_status: value }))} options={orgStatusOptions} placeholder="Filter org status" />
          </Field>
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr_auto_auto]">
          <Field label="User Status">
            <StyledSelect value={filters.user_status} onChange={(value) => setFilters((current) => ({ ...current, user_status: value }))} options={userStatusOptions} placeholder="Filter user status" />
          </Field>
          <div className="hidden lg:block" />
          <button type="button" onClick={() => { setPage(1); load(1); }} className="ems-btn-primary h-12 self-end text-sm">
            Apply Filters
          </button>
          <button
            type="button"
            onClick={() => {
              const clearedFilters = { role: '', org_role: '', org_status: '', user_status: '' };
              setSearch('');
              setFilters(clearedFilters);
              setPage(1);
              load(1, '', clearedFilters);
            }}
            className="ems-btn-secondary h-12 self-end text-sm"
          >
            Reset
          </button>
        </div>
      </section>

      {error ? <Banner tone="error" message={error} /> : null}
      {success ? <Banner tone="success" message={success} /> : null}

      <section className="superadmin-glass rounded-3xl p-4 lg:p-5">
        <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-800">Organization User Directory</p>
            <p className="text-xs text-slate-500">Showing {rows.length} of {meta.total} organization-linked user records.</p>
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-[#fff5f8] px-3 py-1 text-xs font-semibold text-[#9f1239]">
            <Users size={14} /> Role-aware tenant visibility
          </div>
        </div>

        <div className="space-y-3 lg:hidden">
          {loading ? <p className="text-sm text-slate-500">Loading organization users...</p> : null}
          {!loading && rows.length === 0 ? <EmptyState /> : null}
          {!loading && rows.map((row) => (
            <article key={row.membership_id || `${row.id}-${row.organization_id}`} className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-slate-900">{row.name}</p>
                  <p className="text-sm text-slate-500">{row.email}</p>
                  <p className="mt-1 text-xs text-slate-400">{row.phone || 'No phone recorded'}</p>
                </div>
                <StatusChip status={Number(row.is_blocked) ? 'suspended' : 'active'} />
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <Info label="Organization" value={row.organization_name} />
                <Info label="Org Role" value={<OrgRoleChip role={row.org_role} />} />
                <Info label="Global Role" value={<GlobalRoleChip role={row.global_role} />} />
                <Info label="Org Status" value={<StatusChip status={row.organization_status} />} />
                <Info label="Last Login" value={formatDateTime(row.last_login_at)} />
                <Info label="Created" value={formatDate(row.created_at)} />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <ActionIconButton icon={Eye} label="View user" tone="view" onClick={() => setViewRow(row)} />
                <ActionIconButton icon={Pencil} label="Edit user" tone="edit" onClick={() => setEditRow(row)} disabled={row.global_role === 'super_admin'} />
                <ActionIconButton
                  icon={Number(row.is_blocked) ? ShieldCheck : ShieldBan}
                  label={Number(row.is_blocked) ? 'Reactivate user' : 'Suspend user'}
                  tone={Number(row.is_blocked) ? 'success' : 'edit'}
                  onClick={() => handleSuspendToggle(row, !Number(row.is_blocked))}
                  disabled={saving || Number(row.id) === Number(user?.id)}
                />
              </div>
            </article>
          ))}
        </div>

        <div className="hidden overflow-auto lg:block">
          <table className="w-full min-w-[1320px] text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-slate-500">
                <th className="py-3 pr-3">User Name</th>
                <th className="pr-3">Email</th>
                <th className="pr-3">Phone</th>
                <th className="pr-3">Organization Name</th>
                <th className="pr-3">Organization Role</th>
                <th className="pr-3">Global Role</th>
                <th className="pr-3">Status</th>
                <th className="pr-3">Organization Status</th>
                <th className="pr-3">Last Login</th>
                <th className="pr-3">Created Date</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-8 text-center text-slate-500" colSpan={11}>Loading organization users...</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td className="py-8" colSpan={11}><EmptyState compact /></td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.membership_id || `${row.id}-${row.organization_id}`} className="border-b border-slate-50 align-top hover:bg-slate-50/60">
                  <td className="py-4 pr-3">
                    <p className="font-semibold text-slate-900">{row.name}</p>
                    <p className="mt-1 text-xs text-slate-400">Projects: {row.projects_count || 0} · Transactions: {row.transactions_count || 0}</p>
                  </td>
                  <td className="pr-3 text-slate-600">{row.email}</td>
                  <td className="pr-3 text-slate-600">{row.phone || '-'}</td>
                  <td className="pr-3">
                    <p className="font-medium text-slate-800">{row.organization_name}</p>
                    <p className="text-xs text-slate-400">{row.organization_code || '-'}</p>
                  </td>
                  <td className="pr-3"><OrgRoleChip role={row.org_role} /></td>
                  <td className="pr-3"><GlobalRoleChip role={row.global_role} /></td>
                  <td className="pr-3"><StatusChip status={Number(row.is_blocked) ? 'suspended' : 'active'} /></td>
                  <td className="pr-3"><StatusChip status={row.organization_status} /></td>
                  <td className="pr-3 text-slate-600">{formatDateTime(row.last_login_at)}</td>
                  <td className="pr-3 text-slate-600">{formatDate(row.created_at)}</td>
                  <td className="py-4 text-right">
                    <div className="inline-flex flex-wrap justify-end gap-2">
                      <ActionIconButton icon={Eye} label="View user" tone="view" onClick={() => setViewRow(row)} />
                      <ActionIconButton icon={Pencil} label="Edit user" tone="edit" onClick={() => setEditRow(row)} disabled={row.global_role === 'super_admin'} />
                      <ActionIconButton
                        icon={Number(row.is_blocked) ? ShieldCheck : ShieldBan}
                        label={Number(row.is_blocked) ? 'Reactivate user' : 'Suspend user'}
                        tone={Number(row.is_blocked) ? 'success' : 'edit'}
                        onClick={() => handleSuspendToggle(row, !Number(row.is_blocked))}
                        disabled={saving || Number(row.id) === Number(user?.id)}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-col gap-3 border-t border-slate-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-slate-500">Page {page} of {meta.totalPages}</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => load(Math.max(1, page - 1))} disabled={page <= 1 || loading} className="ems-btn-secondary text-sm disabled:opacity-50">Previous</button>
            <button type="button" onClick={() => load(Math.min(meta.totalPages, page + 1))} disabled={page >= meta.totalPages || loading} className="ems-btn-primary text-sm disabled:opacity-50">Next</button>
          </div>
        </div>
      </section>

      <SuperAdminPanel
        open={!!viewRow}
        title={viewRow ? `${viewRow.name} profile` : ''}
        subtitle={viewRow ? `${viewRow.organization_name} · ${viewRow.email}` : ''}
        onClose={() => setViewRow(null)}
      >
        {viewRow ? <UserProfileView row={viewRow} onEdit={() => { setEditRow(viewRow); setViewRow(null); }} currentUserId={user?.id} onSuspend={() => handleSuspendToggle(viewRow, !Number(viewRow.is_blocked))} saving={saving} /> : null}
      </SuperAdminPanel>

      <SuperAdminPanel
        open={!!editRow}
        title={editRow ? `Edit access for ${editRow.name}` : ''}
        subtitle={editRow ? `Organization role remains ${editRow.org_role || 'unassigned'} for ${editRow.organization_name}.` : ''}
        onClose={() => setEditRow(null)}
        footer={editRow ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <button type="button" className="ems-btn-secondary text-sm" onClick={() => setEditRow(null)}>Cancel</button>
            <button type="button" className="ems-btn-secondary text-sm" disabled={saving} onClick={() => handleRoleChange(editRow, editRole)}>Save Role</button>
            <button type="button" className="ems-btn-primary text-sm" disabled={saving} onClick={handleUserUpdate}>Save Profile</button>
          </div>
        ) : null}
      >
        {editRow ? (
          <div className="space-y-5">
            <div className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 sm:grid-cols-2">
              <Info label="Organization" value={editRow.organization_name} />
              <Info label="Organization Role" value={<OrgRoleChip role={editRow.org_role} />} />
              <Info label="Current Global Role" value={<GlobalRoleChip role={editRow.global_role} />} />
              <Info label="User Status" value={<StatusChip status={Number(editRow.is_blocked) ? 'suspended' : 'active'} />} />
            </div>

            <Field label="Global Role">
              <StyledSelect value={editRole} onChange={setEditRole} options={editableRoleOptions} placeholder="Select global role" searchable />
            </Field>

            <div className="rounded-2xl border border-slate-100 bg-white p-4">
              <p className="text-sm font-semibold text-slate-800">Quick actions</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100"
                  disabled={saving || Number(editRow.id) === Number(user?.id)}
                  onClick={() => handleSuspendToggle(editRow, !Number(editRow.is_blocked))}
                >
                  {Number(editRow.is_blocked) ? 'Reactivate user' : 'Suspend user'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="First Name">
                <input
                  value={editForm.first_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                  placeholder="First name"
                />
              </Field>
              <Field label="Last Name">
                <input
                  value={editForm.last_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                  placeholder="Last name"
                />
              </Field>
            </div>
            <Field label="Email Address">
              <input
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                placeholder="Email address"
              />
            </Field>
            <Field label="Phone">
              <input
                value={editForm.phone}
                onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                placeholder="Phone number (optional)"
              />
            </Field>
          </div>
        ) : null}
      </SuperAdminPanel>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label>
      <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">{label}</span>
      {children}
    </label>
  );
}

function Banner({ tone, message }) {
  const style = tone === 'error'
    ? { background: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }
    : { background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' };

  return (
    <div className="rounded-2xl border px-4 py-3 text-sm font-medium" style={style}>
      {message}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.14em] text-slate-400">{label}</p>
      <div className="mt-1 text-sm font-medium text-slate-700">{value}</div>
    </div>
  );
}

function EmptyState({ compact = false }) {
  return (
    <div className={`rounded-2xl border border-dashed border-slate-200 bg-slate-50 text-center ${compact ? 'p-6' : 'p-10'}`}>
      <p className="text-sm font-semibold text-slate-700">No organization users matched the current filters.</p>
      <p className="mt-1 text-sm text-slate-500">Try a broader search or reset the role and status filters.</p>
    </div>
  );
}

function UserProfileView({ row, onEdit, currentUserId, onSuspend, saving }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 gap-4 rounded-2xl border border-slate-100 bg-slate-50/70 p-4 sm:grid-cols-2">
        <Info label="User Name" value={row.name} />
        <Info label="Email" value={row.email} />
        <Info label="Phone" value={row.phone || '-'} />
        <Info label="Organization" value={row.organization_name} />
        <Info label="Organization Role" value={<OrgRoleChip role={row.org_role} />} />
        <Info label="Global Role" value={<GlobalRoleChip role={row.global_role} />} />
        <Info label="User Status" value={<StatusChip status={Number(row.is_blocked) ? 'suspended' : 'active'} />} />
        <Info label="Organization Status" value={<StatusChip status={row.organization_status} />} />
        <Info label="Last Login" value={formatDateTime(row.last_login_at)} />
        <Info label="Created Date" value={formatDate(row.created_at)} />
      </div>

      <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-100 bg-white p-4">
        <Info label="Projects" value={row.projects_count || 0} />
        <Info label="Transactions" value={row.transactions_count || 0} />
      </div>

      <div className="flex flex-wrap gap-2">
        <button type="button" className="ems-btn-secondary text-sm" onClick={onEdit} disabled={row.global_role === 'super_admin'}>
          Edit access
        </button>
        <button
          type="button"
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 transition hover:bg-amber-100 disabled:opacity-50"
          onClick={onSuspend}
          disabled={saving || Number(row.id) === Number(currentUserId)}
        >
          {Number(row.is_blocked) ? 'Reactivate user' : 'Suspend user'}
        </button>
      </div>
    </div>
  );
}